/**
 * Monster Onesie — PDP gallery (BRIEF §5.2).
 *
 * The gallery shows ONE COLOUR'S images at a time. When the configurator
 * announces a colour change it swaps the whole set — track and thumbnail row
 * both — rather than jumping to a single slide inside a mixed list.
 *
 * Colour tagging comes from each media's alt text, parsed in Liquid into
 * data-colours (pipe-joined). The colour part may be a COMMA-SEPARATED LIST in
 * the admin, so one group shot belongs to every gallery it appears in — see
 * THEME-NOTES.md, "Design amendments". A slide with an empty data-colours is
 * untagged and therefore generic: it shows in every colour's gallery.
 *
 * Per §5.2, a colour with no matching media leaves the gallery exactly as it
 * is. Falling back to "show everything" or to slide 0 would silently present
 * the wrong monster, which is worse than not reacting. The same rule is what
 * benches `color:Archived` media: "Archived" matches no Color option value and
 * the tag is not empty, so those photos never enter the visible set. Nothing
 * here special-cases the word — the membership test IS the bench. Any change
 * that treats "no match" as "show everything" un-benches them.
 *
 * NAVIGATION — rewritten 2 Sep 2026. The slides used to be stacked absolute
 * layers cross-faded by a class, which is unswipeable; 85% of traffic is mobile
 * and session recordings showed shoppers rage-clicking the arrows. The slides
 * are now a real horizontal scroll container with CSS scroll-snap, so the
 * native gesture does the work.
 *
 * Consequences worth knowing before editing this file:
 *
 * - The scroll position is the source of truth for "which image is showing".
 *   Nothing here tracks it with scroll-event math; an IntersectionObserver
 *   rooted on the track reports it, and the thumbs follow that.
 * - There is NOT ONE TOUCH LISTENER in this file, deliberately. The browser
 *   owns the gesture. Adding one — and especially preventDefault()-ing one —
 *   is how you break vertical page scrolling over the gallery on iOS.
 * - Arrows and colour jumps drive the same scroll container. They are the only
 *   things that move it programmatically.
 */

/** Slides must own at least this much of the track to count as "showing". */
const ACTIVE_RATIO = 0.6;

/**
 * `behavior: 'instant'` is a late addition to the ScrollBehavior IDL enum.
 * Older WebKit — which is exactly what an in-app Instagram/Facebook WebView can
 * be running — throws a TypeError on the whole options dictionary rather than
 * ignoring the unknown value, which would take out the colour-sync jump. Probe
 * once, then fall back to assigning scrollLeft, which is instant everywhere and
 * has no CSS smooth-scroll to fight (we never set scroll-behavior).
 *
 * @type {boolean | null}
 */
let scrollOptionsSupported = null;

/**
 * @param {HTMLElement} el
 * @param {number} left
 * @param {boolean} smooth
 */
function scrollTrackTo(el, left, smooth) {
  if (scrollOptionsSupported !== false) {
    try {
      el.scrollTo({ left: left, behavior: smooth ? 'smooth' : 'instant' });
      scrollOptionsSupported = true;
      return;
    } catch (error) {
      scrollOptionsSupported = false;
    }
  }
  el.scrollLeft = left;
}

class MonsterGallery extends HTMLElement {
  /** @type {HTMLElement[]} */
  #slides = [];

  /** @type {HTMLButtonElement[]} */
  #thumbs = [];

  /** Indices into #slides that are visible for the current colour. */
  #visible = [];

  /** @type {HTMLElement | null} */
  #track = null;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** Last reported ratio per slide, so the callback can pick the winner. */
  #ratios = new WeakMap();

  #index = 0;
  #thumbCount = 6;

  connectedCallback() {
    this.#track = this.querySelector('[data-track]');
    this.#slides = Array.from(this.querySelectorAll('[data-slide]'));
    this.#thumbs = Array.from(this.querySelectorAll('[data-thumb]'));

    // A template without a gallery block, or a product with no media, must not
    // take the page down on the next line.
    if (!this.#track || this.#slides.length === 0) return;

    this.#thumbCount = Number(this.dataset.thumbCount) || 6;

    // Seed from what the server already rendered, so arrows and thumbs work
    // even if the colour below resolves to nothing.
    this.#visible = this.#slides.reduce((acc, slide, i) => {
      if (!slide.hidden) acc.push(i);
      return acc;
    }, []);

    this.#thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => this.#goTo(this.#visible.indexOf(i), true));
    });

    this.querySelector('[data-prev]')?.addEventListener('click', () => this.#step(-1));
    this.querySelector('[data-next]')?.addEventListener('click', () => this.#step(1));

    // Thumb sync, per the brief: observed, not computed from scroll events.
    // Thresholds carry 0 and 1 as well as the 0.6 gate — without the endpoints
    // a slide that falls back below the gate leaves a stale ratio behind and
    // the "which slide won" comparison reads it as still showing.
    if ('IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver(this.#onIntersect, {
        root: this.#track,
        threshold: [0, ACTIVE_RATIO, 1],
      });
      this.#slides.forEach((slide) => this.#observer?.observe(slide));
    }

    document.addEventListener('monster:color', this.#onColor);

    this.#setColour(this.dataset.colour || '');
  }

  disconnectedCallback() {
    document.removeEventListener('monster:color', this.#onColor);
    this.#observer?.disconnect();
    this.#observer = null;
  }

  get #prefersReducedMotion() {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #onIntersect = (entries) => {
    for (const entry of entries) {
      this.#ratios.set(entry.target, entry.intersectionRatio);
    }

    let winner = -1;
    let best = 0;

    // Only the currently visible set can win — a filtered-out slide is
    // display:none and reports 0, but this also keeps a stale entry from a
    // just-hidden slide out of the comparison.
    for (const slideIndex of this.#visible) {
      const ratio = this.#ratios.get(this.#slides[slideIndex]) ?? 0;
      if (ratio > best) {
        best = ratio;
        winner = slideIndex;
      }
    }

    if (winner === -1 || best < ACTIVE_RATIO) return;

    this.#index = this.#visible.indexOf(winner);
    this.#markThumbs(winner);
  };

  /** @param {Event} event */
  #onColor = (event) => {
    const colour = event instanceof CustomEvent ? event.detail?.color : null;
    if (colour) this.#setColour(colour);
  };

  /** @param {HTMLElement} slide */
  #coloursOf(slide) {
    const raw = slide.dataset.colours || '';
    return raw ? raw.split('|').map((name) => name.trim()) : [];
  }

  /** @param {string} colour */
  #setColour(colour) {
    const matches = this.#slides.reduce((acc, slide, i) => {
      const colours = this.#coloursOf(slide);
      // Untagged media is generic and belongs to every gallery.
      if (colours.length === 0 || colours.includes(colour)) acc.push(i);
      return acc;
    }, []);

    // No media for this colour — leave the gallery untouched (§5.2).
    if (matches.length === 0) return;

    this.#visible = matches.slice(0, this.#thumbCount);

    this.#slides.forEach((slide, i) => {
      slide.hidden = !this.#visible.includes(i);
    });

    this.#thumbs.forEach((thumb, i) => {
      thumb.hidden = !this.#visible.includes(i);
    });

    // Jump, never animate: smooth-scrolling here would sweep the customer
    // through every photo of the colour they just left.
    this.#goTo(0, false);
  }

  /** @param {number} delta */
  #step(delta) {
    const count = this.#visible.length;
    if (count === 0 || !this.#track) return;

    const next = this.#index + delta;

    // Wrap, so prev from the first image lands on the last — the behaviour the
    // fade gallery had. A wrap always jumps, whatever the caller asked for,
    // because smooth-scrolling one would drag the eye across the whole strip
    // backwards to move one image forwards.
    if (next < 0 || next >= count) {
      this.#goTo(((next % count) + count) % count, false);
      return;
    }

    // One slide width, measured off the container, so it stays correct at every
    // breakpoint without a hardcoded number.
    scrollTrackTo(
      this.#track,
      this.#track.scrollLeft + delta * this.#track.clientWidth,
      !this.#prefersReducedMotion
    );
  }

  /**
   * @param {number} position Index within the visible set, not the full list.
   * @param {boolean} smooth
   */
  #goTo(position, smooth) {
    const count = this.#visible.length;
    if (count === 0 || !this.#track) return;

    this.#index = ((position % count) + count) % count;
    const slideIndex = this.#visible[this.#index];

    scrollTrackTo(
      this.#track,
      this.#slides[slideIndex]?.offsetLeft ?? 0,
      smooth && !this.#prefersReducedMotion
    );

    // Seed the thumbs now rather than waiting for the observer. The observer
    // still fires and still owns the steady state — this only removes the frame
    // where a colour change leaves the previous colour's thumb marked.
    this.#markThumbs(slideIndex);
  }

  /** @param {number} slideIndex */
  #markThumbs(slideIndex) {
    this.#thumbs.forEach((thumb, i) => {
      const current = i === slideIndex;
      thumb.classList.toggle('is-active', current);
      thumb.setAttribute('aria-current', current ? 'true' : 'false');
    });
  }
}

if (!customElements.get('monster-gallery')) {
  customElements.define('monster-gallery', MonsterGallery);
}
